import Head from 'next/head';
import React, { Suspense, useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Provider, useSelector } from 'react-redux';
import Layout from '../layouts';
import { useStore } from '../store';
import Router from 'next/router';
import NProgress from 'nprogress';
import '../css/main.css';
import '../css/website.css';
// Customer Site CMS live-canvas isolation seam (Phase 0.4). Imported AFTER
// main.css so it is also later in source order — though the doubled-class
// (0,3,0) selectors already win on specificity alone.
import '../css/site-cms-canvas.css';
// Customer Site CMS inline Tiptap editor prose (Phase 2.3) — mirrors the
// public MarkdownRenderer, brand-neutral, scoped under .tc-site-canvas. After
// the seam so it sits later in source order (it relies on element selectors,
// not utility-class specificity, so order is belt-and-suspenders only).
import '../css/site-cms-tiptap.css';
import '../css/layouts/layout-1.css';
import '../css/animate.css';
import '../css/components/buttons.css';
import 'react-datepicker/dist/react-datepicker.css';
import '../css/components/datepicker.css';
import '../css/components/dropdowns.css';
import '../css/components/forms.css';
import '../css/components/left-sidebar-1/styles-lg.css';
import '../css/components/left-sidebar-1/styles-sm.css';
import '../css/components/modals.css';
import '../css/components/navbar.css';
import '../css/components/nprogress.css';
import '../css/components/recharts.css';
import '../css/components/reports.css';
import '../css/components/right-sidebar.css';
import '../css/components/sliders.css';
import '../css/components/steps.css';
import '../css/components/tables.css';
import '../css/components/tabs.css';
import '../css/components/responsive.css';
import '../css/components/visual-builder.css';
import '../css/components/user-widgets/widget-2.css';
import '../css/components/user-widgets/widget-4.css';
import {
  QueryClient,
  QueryClientProvider,
  Hydrate,
} from '@tanstack/react-query';
import RingLoader from 'react-spinners/RingLoader';

import { ToastContainer } from 'react-toastify';
import HoverTooltips from '../components/ui/HoverTooltips';
import 'react-toastify/dist/ReactToastify.css';
import 'react-quill/dist/quill.snow.css';
import {
  socket,
  disconnectSocket,
  connectAuthenticatedSocket,
} from '../sockets';
import { useRouter } from 'next/router';
import { toast } from 'react-toastify';
import cookie from 'js-cookie';
import Modal from 'react-modal';
import LocationProvider from '../components/providers/LocationProvider';
import MuiDarkModeProvider from '../components/providers/MuiDarkModeProvider';
import { Analytics } from '@vercel/analytics/react';
import { initToastQueue } from '../utils/toastQueue';
import { PostHogProvider } from 'posthog-js/react';
import posthog, { syncSessionRecording } from '../posthog';
import { ConsentProvider, useConsent } from '../consent/ConsentContext';
import ConsentBanner from '../components/consent/ConsentBanner';
import AnalyticsConsentGate from '../components/consent/AnalyticsConsentGate';
import ConsentedScripts from '../components/consent/ConsentedScripts';
import ConsentedIntercom from '../components/consent/ConsentedIntercom';
import { ShopAssessmentPopup } from '../components/landing/v2/ShopAssessmentPopup';
import { recordRoute, markReturnNavigation, ORDER_DETAIL_ROUTE } from '../util/navHistory';
import { isForcedDarkPath } from '../util/marketingRoutes';
import { isInPageNav } from '../util/inPageNav';
import { captureUtms } from '../util/analytics';
import { shouldIdentify } from '../util/analyticsAudience';
import { redactVercelEvent } from '../util/redactUrl';
import { isLocalDemoActive, installLocalDemoTransport, DemoControlBar } from '@localdemo';

function PortaledToastContainer() {
  // Gate the portal on a post-mount flag so the FIRST client render matches
  // the server (both render nothing here). Returning `null` only on the server
  // but a `createPortal(<ToastContainer/>)` on the client's first render is a
  // hydration mismatch: the SSR HTML has no `<div class="Toastify">`, so React
  // throws "Expected server HTML to contain a matching <div>" and falls back
  // to client rendering the whole root. The toast container appears a tick
  // later (after mount) — invisible to the user, no SSR value for toasts.
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  if (!mounted) {
    return null;
  }

  return createPortal(<ToastContainer autoClose={5000} position='top-right' pauseOnHover pauseOnFocusLoss closeOnClick={false} />, document.body);
}

// Identifies the logged-in user + sets the `company` group in PostHog.
// Mounted inside <Provider store> so it can read Redux. The dashboard stores
// the auth payload under state.user as { role, company, user } (see
// redux/user/reducer.js + util/withAuth.js setKeys) — so the actual user
// object is state.user.user, NOT state.user. See POSTHOG_IMPLEMENTATION_GUIDE.md §4.4.
function PostHogIdentity() {
  const userState = useSelector((s) => s.user);
  const { has } = useConsent();
  const analytics = has('analytics');
  const u = userState?.user;
  const c = userState?.company;
  useEffect(() => {
    // Never send PII (email/name) to PostHog without `analytics` consent
    // (GDPR plan P2-1/P7-4). identify/group are suppressed by PostHog while
    // opted-out anyway, but gating here makes it explicit and defensible —
    // and re-runs on opt-in so the user is identified the moment they consent.
    syncSessionRecording({ analytics, userId: u?.id, role: userState?.role, email: u?.email });
    if (!analytics) return;
    if (!u?.id) return;
    if (!shouldIdentify(u)) {
      posthog.opt_out_capturing(); // staff never count (fix-list #16)
      return;
    }
    posthog.identify(String(u.id), {
      email: u.email,
      name: u.fullName,
      username: u.username,
      role: userState?.role,
    });
    if (c?.id) {
      posthog.group('company', String(c.id), {
        name: c.name,
        slug: c.slug,
        plan: c.subscription_plan,
        status: c.subscription_status,
        is_trial: c.is_trial ?? c.subscription_status === 'trialing',
        industry: c.industry,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [analytics, u?.id, c?.id, userState?.role]);
  return null;
}

// Vercel Analytics, gated on `analytics` consent (GDPR plan P2-3). Rendered
// inside <ConsentProvider> so it only mounts (and only loads its script /
// beacon) once analytics consent is granted. `beforeSend` masks URL
// capabilities (?loginToken=, ?code= on reset/redirect, ?access_token=…) — D18.
function ConsentedVercelAnalytics() {
  const { has } = useConsent();
  return has('analytics') ? <Analytics beforeSend={redactVercelEvent} /> : null;
}

// Scroll to top helper. Forces position 0 on the window AND on the inner
// scroll containers used by the dashboard/commerce layouts. Uses
// `behavior: 'instant'` so a global `scroll-behavior: smooth` (if any layout
// re-introduces it) can't animate the jump.
const scrollToTop = () => {
  if (typeof window === 'undefined') return;
  try {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  } catch {
    window.scrollTo(0, 0);
  }
  if (document.scrollingElement) document.scrollingElement.scrollTop = 0;
  document.documentElement.scrollTop = 0;
  document.body.scrollTop = 0;
  // Inner scroll container used by the authenticated dashboard layout.
  const scrollableMain = document.querySelector('.wrapper .main');
  if (scrollableMain) scrollableMain.scrollTop = 0;
  // Inner scroll container used by the commerce layout.
  const commerceMain = document.querySelector('.commerce-layout-main');
  if (commerceMain) commerceMain.scrollTop = 0;
};

// True for navigations that target an in-page anchor (e.g. `/#founder`).
// We skip scroll-to-top for these so anchor scrolling still works.
const isHashNav = (url) =>
  typeof url === 'string' && url.includes('#');

// Set at routeChangeStart for a shallow, same-page push (util/inPageNav.js) so
// the complete handlers below skip the page-navigation chrome too.
let inPageNav = false;

Router.events.on('routeChangeStart', (url, opts) => {
  inPageNav =
    typeof window !== 'undefined' &&
    isInPageNav(url, opts, window.location.pathname);
  if (inPageNav) return;
  NProgress.start();
  // Re-apply the dark-class rule as the new route starts to render so a
  // dashboard→marketing nav (e.g. light dashboard pref → /pricing) doesn't
  // flash light on the marketing page before the store subscriber fires.
  if (typeof window !== 'undefined') {
    try {
      const pathname = url.split('?')[0].split('#')[0];
      const forceDark = isForcedDarkPath(pathname);
      const darkPref = localStorage.getItem('timbercloud-dark-mode');
      const userDark =
        darkPref === 'true' ||
        (darkPref === null &&
          window.matchMedia?.('(prefers-color-scheme: dark)')?.matches);
      if (forceDark || userDark) {
        document.documentElement.classList.add('dark');
      } else {
        document.documentElement.classList.remove('dark');
      }
    } catch {}
  }
  if (isHashNav(url)) return;
  // Scroll BEFORE the new page paints. Without this, navigating from a
  // halfway-scrolled page would leave the new page at the same scroll offset.
  scrollToTop();
});
Router.events.on('routeChangeComplete', (url) => {
  NProgress.done();
  if (isHashNav(url)) return;
  // Visit log for "send me back where I was" redirects (util/navHistory.js).
  // Router.pathname is already the destination's route pattern by this point.
  recordRoute(url, Router.pathname);
  if (inPageNav) return;
  // Belt-and-suspenders: scroll again after the new page has rendered, in
  // case any on-mount layout shift on the destination page nudged scroll.
  scrollToTop();
  requestAnimationFrame(() => {
    requestAnimationFrame(scrollToTop);
  });
});
Router.events.on('routeChangeError', () => NProgress.done());

// The browser's Back (or Forward) off an order is a return too, same as the
// order page's back link: the page it lands on restores its filters and
// preset (util/navHistory.js). At popstate the URL already names the
// destination while Router.pathname is still the order being left.
if (typeof window !== 'undefined') {
  window.addEventListener('popstate', () => {
    try {
      if (Router.pathname === ORDER_DETAIL_ROUTE) {
        markReturnNavigation(window.location.pathname);
      }
    } catch {
      // Router not mounted yet — nothing to return from.
    }
  });
}

// Disable browser's automatic scroll restoration
if (typeof window !== 'undefined' && 'scrollRestoration' in window.history) {
  window.history.scrollRestoration = 'manual';
}

// Query client is now created inside App component

// List of public routes that don't require authentication
const publicRoutes = [
  '/',
  '/login',
  '/register',
  '/forgot-password',
  '/reset-password',
  '/session-conflict',
  '/request-access',
  '/landing',
  '/magic-login',
  // Free-tier store-setup: user is authenticated but company-less, so the socket
  // layer already treats them as public (isAuthenticated needs a company cookie).
  // Listed for consistency; the load-bearing routing lives in layouts/index.js.
  '/onboarding/free',
  // OAuth return page is dynamic (/connect/[provider]/redirect) — matched by
  // the startsWith('/connect') checks below.
  // Add any other public routes here
];

// Marketing and authentication routes default to dark mode — the toggle has been
// removed from these pages, so the dashboard's user preference does not apply
// here. Returning dashboard users with a light preference still see dark on
// these public pages; this is intentional. util/marketingRoutes.js keeps this
// theme check separate from marketing-only chrome like IwfSitewidePopup.

// Prevent flash of wrong theme — apply dark class before React hydrates
if (typeof window !== 'undefined') {
  try {
    const darkPref = localStorage.getItem('timbercloud-dark-mode');
    const isPublicTheme = isForcedDarkPath(window.location.pathname);
    const isDark =
      isPublicTheme ||
      darkPref === 'true' ||
      (darkPref === null &&
        window.matchMedia?.('(prefers-color-scheme: dark)')?.matches);
    if (isDark) {
      document.documentElement.classList.add('dark');
    }
  } catch {}
}

export default function App({ Component, pageProps }) {
  const store = useStore(pageProps.initialReduxState);

  // Hydrate Redux dark mode state and keep DOM + Redux in sync
  useEffect(() => {
    // 1. Hydrate Redux from localStorage/system preference
    store.dispatch({ type: 'HYDRATE_DARK_MODE' });
    // Restore the desktop sidebar preference, or close the mobile drawer.
    // Runs before the showChild effect flips so the dashboard's first paint
    // already has the right sidebar width.
    store.dispatch({ type: 'HYDRATE_SIDEBAR_COLLAPSED' });

    // 2. Subscribe to Redux store to sync DOM dark class with state changes.
    //    Marketing routes are pinned to dark regardless of Redux state — the
    //    toggle has been removed from those surfaces, so the dashboard's user
    //    preference does not apply.
    const unsubscribe = store.subscribe(() => {
      const { darkMode } = store.getState().palettes;
      const forceDark = isForcedDarkPath(window.location.pathname);
      if (darkMode || forceDark) {
        document.documentElement.classList.add('dark');
      } else {
        document.documentElement.classList.remove('dark');
      }
    });

    // 3. Listen for cross-tab localStorage changes
    const handleStorage = (e) => {
      if (e.key === 'timbercloud-dark-mode') {
        const isDark = e.newValue === 'true';
        store.dispatch({ type: 'SET_DARK_MODE', value: isDark });
      }
    };
    window.addEventListener('storage', handleStorage);

    // 4. Listen for system dark mode changes (e.g. device switches at sunset)
    //    Only follow system preference if user hasn't explicitly set one
    const mediaQuery = window.matchMedia?.('(prefers-color-scheme: dark)');
    const handleSystemThemeChange = (e) => {
      const hasUserPref =
        localStorage.getItem('timbercloud-dark-mode') !== null;
      if (!hasUserPref) {
        store.dispatch({ type: 'SET_DARK_MODE_FROM_SYSTEM', value: e.matches });
      }
    };
    mediaQuery?.addEventListener?.('change', handleSystemThemeChange);

    return () => {
      unsubscribe();
      window.removeEventListener('storage', handleStorage);
      mediaQuery?.removeEventListener?.('change', handleSystemThemeChange);
    };
  }, [store]);
  const [showChild, setShowChild] = useState(false);
  const router = useRouter();
  const [queryClient] = useState(() => {
    const client = new QueryClient({
      defaultOptions: {
        queries: {
          refetchOnWindowFocus: false,
          retry: 1,
        },
        mutations: {
          onError: (error) => {
            const status =
              error?.status ||
              error?.statusCode ||
              error?.error?.status ||
              error?.error?.statusCode ||
              error?.response?.status ||
              error?.response?.statusCode ||
              error?.response?.data?.error?.status;
            if (status === 403) {
              toast.error(
                'You do not have permission to perform this action.',
                {
                  position: toast.POSITION.TOP_RIGHT,
                  toastId: 'permission-denied',
                },
              );
            }
          },
        },
      },
    });
    initToastQueue(client);
    return client;
  });

  // Set the app element for react-modal accessibility
  useEffect(() => {
    Modal.setAppElement('body');
  }, []);

  useEffect(() => {
    setShowChild(true);
  }, []);

  // First-touch UTM capture for the GA4 key events (Q0 acquisition plan).
  // Mount-only, deliberately: this runs on the FIRST page load of the session,
  // which is the touch we want to credit. Re-running it on every route change
  // would be a no-op anyway (captureUtms never overwrites a stored set), but
  // tying it to mount makes the first-touch intent explicit.
  //
  // Runs OUTSIDE the consent gate: it reads the URL the visitor arrived on and
  // writes sessionStorage, no cookies and no third party, so it carries no
  // consent obligation. The events that consume it are gated by whether gtag
  // is present, not by this.
  useEffect(() => {
    captureUtms();
  }, []);

  // ---- PostHog analytics ----
  // Init + $pageview capture + the tc.* → PostHog bridge are now CONSENT-GATED
  // and live in <AnalyticsConsentGate/> (mounted below, inside ConsentProvider).
  // PostHog is init'd opted-out-by-default (see src/posthog.js) and captures
  // nothing until `analytics` consent is granted. GDPR plan P2-1.

  // Check if the current route is public
  const isPublicRoute = useCallback(() => {
    return (
      publicRoutes.includes(router.pathname) ||
      router.pathname.startsWith('/landing') ||
      router.pathname.startsWith('/connect') ||
      router.pathname.startsWith('/magic') ||
      router.pathname.includes('/session-conflict')
    );
  }, [router.pathname]);

  // Check if user is authenticated. Excludes local demo mode: its sentinel
  // cookies would otherwise read as authenticated here and attempt a real
  // socket.io connection against NEXT_PUBLIC_DB_URL, which just churns
  // reconnect failures since there's no real session behind them.
  const isAuthenticated = () => {
    return !isLocalDemoActive() && !!cookie.get('jwt') && !!cookie.get('company');
  };

  // Local demo mode: install the fixture axios adapter once per mount, before
  // any query fires. isLocalDemoActive() (and this whole import, in a
  // production build) is a compile-time false — see @localdemo's README.
  useEffect(() => {
    if (isLocalDemoActive()) installLocalDemoTransport();
  }, []);

  useEffect(() => {
    // Only connect to socket if user is authenticated and not on a public route
    if (
      !isPublicRoute() &&
      isAuthenticated() &&
      !router.pathname.includes('session-conflict')
    ) {
      console.log('Connecting authenticated socket for page:', router.pathname);
      connectAuthenticatedSocket();
    } else {
      // For public routes, we can either disconnect or connect without registration
      console.log('On public page:', router.pathname);
      if (socket.connected) {
        disconnectSocket();
      }
    }

    // Setup session conflict handler - do this even before authentications are checked
    // Clean up any existing listener first to prevent duplicates
    socket.off('session-conflict');

    // Add the listener - this should be active for all scenarios
    socket.on('session-conflict', (data) => {
      console.log('⚠️ RECEIVED SESSION-CONFLICT EVENT:', data);

      // Show a toast notification
      toast.error(
        data.message ||
          'Your session has been terminated because you logged in from another device.',
        {
          autoClose: 3000,
          onClose: () => {
            console.log('Toast closed, redirecting to session conflict page');
          },
        },
      );

      // Force disconnect the socket first to prevent reconnection attempts
      disconnectSocket();

      // Clear cookies
      cookie.remove('jwt');
      cookie.remove('role');
      cookie.remove('company');

      // Clear localStorage and sessionStorage
      try {
        localStorage.removeItem('user');
        sessionStorage.removeItem('sessionChecked');
      } catch (e) {
        console.error('Error removing user data from storage:', e);
      }

      // Get company slug from the event data if available
      const companySlug = data.company?.slug;

      // Redirect to session conflict page with company slug if available
      router.push(
        companySlug
          ? `/session-conflict?company=${companySlug}`
          : '/session-conflict',
      );
    });

    // Set up a heartbeat to keep the session alive only when authenticated
    let heartbeatInterval;
    if (isAuthenticated() && socket.connected) {
      heartbeatInterval = setInterval(() => {
        if (socket.connected) {
          socket.emit('user-activity');
          console.log(
            'Sending heartbeat signal to server to keep session alive',
          );
        }
      }, 15000); // Send heartbeat every 15 seconds to prevent false session warnings
    }

    return () => {
      socket.off('session-conflict');
      if (heartbeatInterval) {
        clearInterval(heartbeatInterval);
      }
    };
  }, [router.pathname, router, isPublicRoute]);

  // Seed the visit log with the landing page. `routeChangeComplete` only fires
  // for client-side navigations, so without this the first page of a session —
  // often the very page a "take me back" redirect wants to skip — is missing.
  useEffect(() => {
    recordRoute(
      window.location.pathname + window.location.search,
      Router.pathname
    );
  }, []);

  // Listen for route changes to connect/disconnect socket as needed
  useEffect(() => {
    const handleRouteChange = (url) => {
      // Same page, same socket: re-registering on every tab click is noise.
      if (inPageNav) return;
      const isPublic = publicRoutes.some(
        (route) =>
          url === route ||
          url.startsWith('/landing') ||
          url.startsWith('/connect') ||
          url.startsWith('/magic') ||
          url.includes('/session-conflict'),
      );

      if (!isPublic && isAuthenticated()) {
        console.log(
          'Route changed to authenticated page, connecting authenticated socket',
        );
        connectAuthenticatedSocket();
      } else if (isPublic) {
        console.log('Route changed to public page, disconnecting socket');
        disconnectSocket();
      }
    };

    Router.events.on('routeChangeComplete', handleRouteChange);

    return () => {
      Router.events.off('routeChangeComplete', handleRouteChange);
    };
  }, []);

  // Hydration shield for the authenticated dashboard: it reads
  // localStorage/cookies in ways that differ between server and client, so
  // by default we skip SSR entirely and render only after mount. Public
  // marketing pages OPT OUT of the shield by setting a static
  // `ssrEnabled = true` on their page component — their server HTML must
  // contain the real body and per-page <Head> tags or crawlers and link
  // scrapers see an empty page (see SEO_INDEXABILITY_ANALYSIS_2026-06-12.md
  // RC-1). Opted-in pages must stay hydration-safe: no unguarded browser
  // API reads during render.
  if (!showChild && !Component.ssrEnabled) {
    return null;
  }

  // Use the getLayout function from the Component if it exists, otherwise use the default Layout
  const getLayout =
    Component.getLayout ||
    ((page) => (
      <Layout>
        <Suspense
          fallback={
            <div className='flex flex-col items-center justify-center min-h-screen gap-4'>
              <RingLoader size={90} color={'#006B45'} speedMultiplier={1.2} />
              <p className='text-gray-700 font-medium animate-pulse'>
                Loading...
              </p>
            </div>
          }
        >
          {page}
        </Suspense>
      </Layout>
    ));

  return (
    <>
      <Head>
        <meta
          name='viewport'
          content='width=device-width, initial-scale=1, shrink-to-fit=no'
        />
      </Head>
      <QueryClientProvider client={queryClient}>
        <Hydrate state={pageProps.dehydratedState}>
          <Provider store={store}>
            <ConsentProvider>
              <MuiDarkModeProvider>
                <LocationProvider>
                  <PostHogProvider client={posthog}>
                    <PostHogIdentity />
                    {/* Consent → analytics side-effects (PostHog opt-in/out,
                        $pageview, tc.* bridge) + consent-gated sitewide scripts
                        (Cloudflare/Supademo). GDPR plan P2-1/P2-3/P2-5. */}
                    <AnalyticsConsentGate />
                    <ConsentedScripts />
                    {/* Anonymous Intercom on every public page, marketing
                        consent only. Dashboard identify lives in Layout1. */}
                    <ConsentedIntercom />
                    {getLayout(
                      <>
                        <Component {...pageProps} />
                        <PortaledToastContainer />
                        <HoverTooltips />
                        <DemoControlBar />
                        <ConsentedVercelAnalytics />
                      </>,
                    )}
                    {/* Public marketing routes only; the component also skips
                        the assessment itself and every authenticated surface. */}
                    <ShopAssessmentPopup />
                    {/* First-party consent banner + preference center. */}
                    <ConsentBanner />
                  </PostHogProvider>
                </LocationProvider>
              </MuiDarkModeProvider>
            </ConsentProvider>
          </Provider>
        </Hydrate>
      </QueryClientProvider>
    </>
  );
}
